import React, { useState, useEffect, useContext, useRef, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import AuthContext from '../context/AuthContext';
import { useSettings } from '../context/SettingsContext';
import {
  Compass,
  Zap,
  Sliders,
  LayoutTemplate,
  Save,
  Mail,
  Sparkles,
  Users,
  Ticket,
  CheckCircle2,
  Minimize2,
  Maximize2,
  MessageSquare,
  Send,
  X,
  ChevronRight,
  ChevronLeft,
  HelpCircle,
  Info
} from 'lucide-react';

const STORAGE_KEY_COMPLETED = 'pfadi_admin_tour_completed_v2_spotlight';
const STORAGE_KEY_STEP = 'pfadi_admin_tour_current_step_v2_spotlight';
const STORAGE_KEY_DISMISSED = 'pfadi_admin_tour_dismissed_until_v2_spotlight';

export default function AdminOnboardingTour({ isOpen: forcedOpen, onClose: onForcedClose, initialStep = 0 }) {
  const { user } = useContext(AuthContext) || {};
  const { organization, settings } = useSettings();
  const navigate = useNavigate();
  const location = useLocation();

  const [isOpen, setIsOpen] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [currentStep, setCurrentStep] = useState(initialStep);
  const [targetRect, setTargetRect] = useState(null);
  const [isNavigating, setIsNavigating] = useState(false);

  // Devino Question / Pause Modal State
  const [isQuestionModalOpen, setIsQuestionModalOpen] = useState(false);
  const [questionText, setQuestionText] = useState('');
  const [authorName, setAuthorName] = useState(user?.name || user?.username || '');
  const [authorEmail, setAuthorEmail] = useState(user?.email || '');
  const [isSubmittingQuestion, setIsSubmittingQuestion] = useState(false);
  const [questionSuccess, setQuestionSuccess] = useState(false);
  const [questionError, setQuestionError] = useState(null);

  const stammName = organization?.stammName || settings?.stammName || 'Pfadfinder';
  const isBeta = settings?.isBeta || (typeof window !== 'undefined' && window.location.hostname.includes('beta'));

  // Pre-fill user data
  useEffect(() => {
    if (user) {
      if (user.name) setAuthorName(user.name);
      if (user.email) setAuthorEmail(user.email);
    }
  }, [user]);

  // -------------------------------------------------------------
  // TOUR STATIONS (Pfeile & Spotlight)
  // -------------------------------------------------------------
  const steps = [
    {
      id: 'welcome',
      page: '/',
      target: '[data-tour="dashboard-welcome"]',
      placement: 'bottom',
      icon: Compass,
      badge: 'Station 1 von 10',
      title: 'Willkommen in deiner Leitstelle',
      subtitle: `Digitale Schaltzentrale für ${stammName}`,
      text: 'Hier im Dashboard behältst du den Überblick über alle aktuellen Aktivitäten, Termine und Mitteilungen deines Stammes.',
      tip: 'Diese Tour führt dich direkt durch alle Kernbereiche und zeigt dir Schritt für Schritt, wo du welche Einstellungen vornimmst.',
      btnNextText: 'Zu den Schnellaktionen'
    },
    {
      id: 'quickactions',
      page: '/',
      target: '[data-tour="dashboard-quickactions"]',
      placement: 'bottom',
      icon: Zap,
      badge: 'Station 2 von 10',
      title: 'Schnellaktionen im Alltag',
      subtitle: 'Events, Blogartikel & Aufgaben',
      text: 'Hier kannst du mit einem Klick neue Zeltlager anlegen, Blogbeiträge veröffentlichen oder Aufgaben für dein Team erstellen.',
      tip: 'Gruppenleiter und Vorstände haben hier die schnellsten Abkürzungen für alle täglichen Verwaltungsaufgaben.',
      btnNextText: 'Zu den Admin-Tools'
    },
    {
      id: 'sidebar-admin',
      page: '/',
      target: '[data-tour="nav-admin-tools"]',
      placement: 'right',
      icon: Sliders,
      badge: 'Station 3 von 10',
      title: 'Admin-Tools & Einstellungen',
      subtitle: 'Baukasten, Codes & Schnittstellen',
      text: 'Über diesen Menüpunkt erreichst du alle administrativen Werkzeuge: Websitebaukasten, Registrierungscodes, API-Schlüssel und Audit-Logs.',
      tip: 'Wir wechseln jetzt automatisch zum Websitebaukasten.',
      btnNextText: 'Zum Websitebaukasten wechseln'
    },
    {
      id: 'homepage-tabs',
      page: '/admin-tools/homepage',
      target: '[data-tour="homepage-tabs"]',
      placement: 'bottom',
      icon: LayoutTemplate,
      badge: 'Station 4 von 10',
      title: 'Startseiten-Baukasten & 5 Kernbereiche',
      subtitle: 'Inhalte, Module, Stammesdaten & Design',
      text: 'Hier gestaltest du eure öffentliche Website. Die 5 Bereiche führen dich von der Sektionen-Reihenfolge über Stammeswappen und Impressum bis hin zum Farbschema.',
      tip: 'Unter "Design & Farben" berechnet das System aus eurem Logo automatisch passende Dark- und Light-Mode Kontrastfarben.',
      btnNextText: 'Zum Speichern-Button'
    },
    {
      id: 'homepage-save',
      page: '/admin-tools/homepage',
      target: '[data-tour="homepage-save"]',
      placement: 'bottom',
      icon: Save,
      badge: 'Station 5 von 10',
      title: 'Änderungen sofort veröffentlichen',
      subtitle: 'Live-Aktualisierung',
      text: 'Wenn du Texte, Logos oder Sektionen geändert hast, klicke hier oben rechts auf "Speichern". Die Website wird ohne Verzögerung direkt aktualisiert.',
      tip: 'Als Nächstes schauen wir uns die Schnittstellen für E-Mails und den KI-Assistenten an.',
      btnNextText: 'Zu den Schnittstellen & Secrets'
    },
    {
      id: 'integration-email',
      page: '/admin-tools/integrations',
      target: '[data-tour="integration-email"]',
      placement: 'bottom',
      icon: Mail,
      badge: 'Station 6 von 10',
      title: 'E-Mail-Versand (Resend API)',
      subtitle: 'Bestätigungen & Einladungen',
      text: 'Trage hier deinen Resend API-Key und eure Absender-E-Mail ein. Das System versendet darüber automatisch Event-Bestätigungen, Elternmails und Passwort-Reset-Links.',
      tip: 'Ohne Resend können keine Einladungs- und Registrierungs-Mails versendet werden.',
      btnNextText: 'Zum KI-Assistenten'
    },
    {
      id: 'integration-ai',
      page: '/admin-tools/integrations',
      target: '[data-tour="integration-ai"]',
      placement: 'top',
      icon: Sparkles,
      badge: 'Station 7 von 10',
      title: 'KI-Assistent & Google Gemini',
      subtitle: 'Bob Chatbot & Textentwürfe',
      text: 'Hinterlege hier euren Google Gemini API-Key. Damit beantwortet euer Pfadfinder-Bot "Bob" Besucherfragen auf der Website und hilft im Dashboard beim Verfassen von Texten.',
      tip: 'Unter "Admin-Tools > Bob & Prompts" kannst du Bob detailliertes Stammeswissen beibringen.',
      btnNextText: 'Zu den Gruppen & Stufen'
    },
    {
      id: 'group-create',
      page: '/groups',
      target: '[data-tour="group-create-btn"]',
      placement: 'bottom',
      icon: Users,
      badge: 'Station 8 von 10',
      title: 'Gruppen, Sippen & Stufen anlegen',
      subtitle: 'Wölflinge, Jungpfadfinder, Pfadfinder, Rover & Leiterrunde',
      text: 'Hier bildest du eure Stammesstruktur ab. Lege Gruppen an, weise Gruppenleiter zu und hinterlege Treffzeiten. Leiter erhalten Zugriff auf die digitale Anwesenheitsliste.',
      tip: 'Gruppenleiter können ihre eigene Sippenseite selbst pflegen, ohne Admin-Rechte zu benötigen.',
      btnNextText: 'Zu den Registrierungscodes'
    },
    {
      id: 'codes-create',
      page: '/admin-tools/codes',
      target: '[data-tour="codes-create-box"]',
      placement: 'bottom',
      icon: Ticket,
      badge: 'Station 9 von 10',
      title: 'Einladungscodes generieren',
      subtitle: 'Beitritt für Leiter & Eltern',
      text: 'Erstelle hier Registrierungscodes (z. B. "LEITER-2026"). Bei der Registrierung mit diesem Code erhalten Nutzer direkt die zugewiesene Rolle und Gruppe.',
      tip: 'Verteile den Admin-Code nur an die Stammesführung. Für Leiter und Eltern gibt es eigene Rollen.',
      btnNextText: 'Abschluss & Zusammenfassung'
    },
    {
      id: 'finish',
      page: '/',
      target: null, // Center dialog
      placement: 'center',
      icon: CheckCircle2,
      badge: 'Geschafft',
      title: 'Du bist startklar!',
      subtitle: 'Einrichtung erfolgreich abgeschlossen',
      text: 'Du kennst nun alle zentralen Schaltstellen deines Stammes. Du kannst diese Tour jederzeit in den Admin-Tools oder über die Schnellauswahl erneut starten.',
      tip: 'Gut Pfad! Falls du Unterstützung benötigst, kannst du jederzeit eine Nachricht an das Entwickler-Team senden.',
      btnNextText: 'Tour abschließen'
    }
  ];

  const activeStep = steps[currentStep] || steps[0];
  const StepIcon = activeStep.icon || Compass;

  // -------------------------------------------------------------
  // EVENT LISTENER FOR GLOBAL TOUR TRIGGER
  // -------------------------------------------------------------
  useEffect(() => {
    const handleStartTour = (e) => {
      const stepIdx = typeof e.detail?.step === 'number' ? e.detail.step : 0;
      setCurrentStep(stepIdx);
      setIsMinimized(false);
      setIsOpen(true);
    };

    window.addEventListener('start-admin-tour', handleStartTour);
    return () => window.removeEventListener('start-admin-tour', handleStartTour);
  }, []);

  // First-time trigger check
  useEffect(() => {
    if (forcedOpen !== undefined) {
      setIsOpen(forcedOpen);
      if (initialStep !== undefined) setCurrentStep(initialStep);
      return;
    }

    if (!user || user.role !== 'admin') {
      setIsOpen(false);
      return;
    }

    try {
      const isCompleted = localStorage.getItem(STORAGE_KEY_COMPLETED) === 'true';
      const dismissedUntil = localStorage.getItem(STORAGE_KEY_DISMISSED);

      if (isCompleted) {
        setIsOpen(false);
        return;
      }

      if (dismissedUntil && Number(dismissedUntil) > Date.now()) {
        setIsOpen(false);
        return;
      }

      const savedStep = localStorage.getItem(STORAGE_KEY_STEP);
      if (savedStep !== null && !isNaN(savedStep)) {
        setCurrentStep(Math.max(0, parseInt(savedStep, 10)));
      }

      const timer = setTimeout(() => {
        setIsOpen(true);
      }, 600);

      return () => clearTimeout(timer);
    } catch (e) {
      console.warn('Admin tour storage check:', e);
    }
  }, [user, forcedOpen, initialStep]);

  // -------------------------------------------------------------
  // UPDATE TARGET RECT ON SCROLL, RESIZE & ROUTE CHANGE
  // -------------------------------------------------------------
  const updateRect = useCallback(() => {
    if (!isOpen || isMinimized) return;
    if (!activeStep.target) {
      setTargetRect(null);
      return;
    }

    const el = document.querySelector(activeStep.target);
    if (el) {
      const rect = el.getBoundingClientRect();
      setTargetRect({
        top: rect.top,
        left: rect.left,
        width: rect.width,
        height: rect.height,
        bottom: rect.bottom,
        right: rect.right
      });
    } else {
      setTargetRect(null);
    }
  }, [isOpen, isMinimized, activeStep]);

  // Handle Page Navigation & Element Polling
  useEffect(() => {
    if (!isOpen || isMinimized) return;

    if (activeStep.page && location.pathname !== activeStep.page) {
      setIsNavigating(true);
      navigate(activeStep.page);
      return;
    }

    setIsNavigating(false);

    let attempts = 0;
    const maxAttempts = 60; // 6 seconds for async data-loading pages
    const pollInterval = setInterval(() => {
      attempts++;
      if (!activeStep.target) {
        setTargetRect(null);
        clearInterval(pollInterval);
        return;
      }

      const el = document.querySelector(activeStep.target);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'center' });
        updateRect();
        clearInterval(pollInterval);
      } else if (attempts >= maxAttempts) {
        setTargetRect(null);
        clearInterval(pollInterval);
      }
    }, 100);

    return () => clearInterval(pollInterval);
  }, [isOpen, isMinimized, currentStep, activeStep, location.pathname, navigate, updateRect]);

  // Listen to window resize and scroll
  useEffect(() => {
    if (!isOpen || isMinimized) return;
    window.addEventListener('resize', updateRect);
    window.addEventListener('scroll', updateRect, true);
    return () => {
      window.removeEventListener('resize', updateRect);
      window.removeEventListener('scroll', updateRect, true);
    };
  }, [isOpen, isMinimized, updateRect]);

  // Keyboard navigation
  useEffect(() => {
    if (!isOpen || isMinimized || isQuestionModalOpen) return;

    const handleKeyDown = (e) => {
      if (['INPUT', 'TEXTAREA'].includes(document.activeElement?.tagName)) return;

      if (e.key === 'Escape') {
        setIsMinimized(true);
      } else if (e.key === 'ArrowRight') {
        handleNext();
      } else if (e.key === 'ArrowLeft') {
        handlePrev();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, isMinimized, isQuestionModalOpen, currentStep]);

  // Navigation handlers
  const handleNext = () => {
    if (currentStep < steps.length - 1) {
      const next = currentStep + 1;
      setCurrentStep(next);
      try {
        localStorage.setItem(STORAGE_KEY_STEP, String(next));
      } catch (e) {}
    } else {
      handleCompleteTour();
    }
  };

  const handlePrev = () => {
    if (currentStep > 0) {
      const prev = currentStep - 1;
      setCurrentStep(prev);
      try {
        localStorage.setItem(STORAGE_KEY_STEP, String(prev));
      } catch (e) {}
    }
  };

  const handleStepJump = (idx) => {
    if (idx >= 0 && idx < steps.length) {
      setCurrentStep(idx);
      try {
        localStorage.setItem(STORAGE_KEY_STEP, String(idx));
      } catch (e) {}
    }
  };

  const handleCompleteTour = () => {
    try {
      localStorage.setItem(STORAGE_KEY_COMPLETED, 'true');
      localStorage.removeItem(STORAGE_KEY_STEP);
    } catch (e) {}
    setIsOpen(false);
    onForcedClose?.();
    navigate('/');
  };

  const handleDismissLater = () => {
    try {
      localStorage.setItem(STORAGE_KEY_DISMISSED, String(Date.now() + 2 * 24 * 60 * 60 * 1000));
    } catch (e) {}
    setIsOpen(false);
    onForcedClose?.();
  };

  // -------------------------------------------------------------
  // SEND MESSAGE / QUESTION TO DEVINO VIA FEEDBACK ROUTE
  // -------------------------------------------------------------
  const handleOpenQuestionModal = () => {
    setQuestionError(null);
    setQuestionSuccess(false);
    setQuestionText('');
    setIsQuestionModalOpen(true);
  };

  const handleSubmitQuestion = async (e) => {
    e.preventDefault();
    if (!questionText.trim()) {
      setQuestionError('Bitte gib deine Frage oder dein Anliegen ein.');
      return;
    }

    setIsSubmittingQuestion(true);
    setQuestionError(null);

    const payload = {
      rating: null,
      category: 'question',
      message: `[SPOTLIGHT ONBOARDING FRAGE - ${activeStep.badge}: ${activeStep.title}]\nElement: ${activeStep.target || 'Zentral'}\nSeite: ${activeStep.page}\n\n${questionText.trim()}`,
      pageUrl: window.location.href,
      pageTitle: `Admin Tour: ${activeStep.title}`,
      tenantSlug: isBeta ? 'beta' : (window.location.hostname.includes('wutoeschingen') ? 'wutoeschingen' : window.location.hostname.split('.')[0]),
      tenantName: `${stammName} (Admin Spotlight Tour)`,
      authorName: authorName.trim() || (user ? `${user.name || user.username || 'Admin'}` : 'Admin'),
      authorEmail: authorEmail.trim() || undefined,
      deviceInfo: {
        screenWidth: window.innerWidth,
        screenHeight: window.innerHeight,
        userAgent: navigator.userAgent,
        pathname: window.location.pathname,
        userRole: user?.role,
        tourStepId: activeStep.id,
        tourStepIndex: currentStep,
        targetSelector: activeStep.target
      }
    };

    try {
      let res = await fetch('/api/feedback', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      }).catch(() => null);

      if (!res || !res.ok) {
        res = await fetch('https://pfadfinder-manager.suppenchris.workers.dev/api/feedback', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload)
        });
      }

      if (res && res.ok) {
        setQuestionSuccess(true);
      } else {
        const data = await res?.json().catch(() => ({}));
        throw new Error(data?.error || 'Nachricht konnte nicht übermittelt werden.');
      }
    } catch (err) {
      console.error('Failed to send question to Devino:', err);
      setQuestionError('Konnte Nachricht nicht senden. Bitte prüfe deine Internetverbindung.');
    } finally {
      setIsSubmittingQuestion(false);
    }
  };

  if (!isOpen) return null;

  // -------------------------------------------------------------
  // MINIMIZED FLOATING PILL
  // -------------------------------------------------------------
  if (isMinimized) {
    return (
      <div className="fixed bottom-24 right-5 sm:bottom-6 sm:right-28 z-[99999] animate-bounce-in max-w-[calc(100vw-3rem)]">
        <div className="flex items-center gap-2 p-2.5 pr-3.5 rounded-2xl bg-surface-containerHighest/95 backdrop-blur-md border border-primary/40 shadow-2xl text-surface-on hover:border-primary transition-all">
          <button
            onClick={() => setIsMinimized(false)}
            className="flex items-center gap-2.5 text-left cursor-pointer group min-w-0"
          >
            <div className="w-8 h-8 rounded-xl bg-primary text-primary-on flex items-center justify-center font-bold text-sm shadow-sm group-hover:scale-105 transition-transform shrink-0">
              <Compass className="w-4 h-4" />
            </div>
            <div className="min-w-0">
              <div className="flex items-center gap-1.5">
                <span className="text-[10px] font-bold uppercase tracking-wider text-primary truncate">Admin-Tour</span>
                <span className="text-[9px] bg-primary/10 text-primary font-bold px-1.5 py-0.2 rounded-md shrink-0">
                  {currentStep + 1}/{steps.length}
                </span>
              </div>
              <p className="text-xs font-bold text-surface-on truncate max-w-[180px] sm:max-w-[220px]">
                {activeStep.title}
              </p>
            </div>
          </button>

          <div className="flex items-center gap-1 border-l border-outlineVariant/30 pl-2 ml-1 shrink-0">
            <button
              onClick={() => setIsMinimized(false)}
              className="p-1.5 text-primary hover:bg-primary/10 rounded-lg transition-colors cursor-pointer"
              title="Tour fortsetzen"
            >
              <Maximize2 className="w-4 h-4" />
            </button>
            <button
              onClick={handleOpenQuestionModal}
              className="p-1.5 text-amber-500 hover:bg-amber-500/10 rounded-lg transition-colors cursor-pointer"
              title="Frage stellen"
            >
              <MessageSquare className="w-4 h-4" />
            </button>
            <button
              onClick={() => setIsOpen(false)}
              className="p-1.5 text-surface-onVariant hover:text-surface-on rounded-lg transition-colors cursor-pointer"
              title="Schließen"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>
    );
  }

  // -------------------------------------------------------------
  // CALCULATE POPOVER POSITION RELATIVE TO HIGHLIGHTED ELEMENT
  // -------------------------------------------------------------
  const popoverWidth = 430;
  const padding = 16;
  const viewportWidth = typeof window !== 'undefined' ? window.innerWidth : 1200;
  const viewportHeight = typeof window !== 'undefined' ? window.innerHeight : 800;

  let popoverStyle = {};
  let arrowDirection = 'up';
  let arrowStyle = {};

  if (!targetRect || activeStep.placement === 'center') {
    // Center Dialog placement
    popoverStyle = {
      position: 'fixed',
      top: '50%',
      left: '50%',
      transform: 'translate(-50%, -50%)',
      width: `min(${popoverWidth}px, calc(100vw - 32px))`,
      zIndex: 100000
    };
    arrowDirection = 'none';
  } else {
    // Target element placement
    const targetCenterX = targetRect.left + targetRect.width / 2;
    const targetCenterY = targetRect.top + targetRect.height / 2;

    let placement = activeStep.placement || 'bottom';

    // Auto adjust placement if target is too close to viewport edges
    if (placement === 'bottom' && targetRect.bottom + 340 > viewportHeight) {
      placement = 'top';
    } else if (placement === 'top' && targetRect.top - 340 < 0) {
      placement = 'bottom';
    }

    if (placement === 'bottom') {
      const top = Math.min(targetRect.bottom + padding, viewportHeight - 350);
      const left = Math.max(16, Math.min(viewportWidth - popoverWidth - 16, targetCenterX - popoverWidth / 2));
      popoverStyle = {
        position: 'fixed',
        top: `${top}px`,
        left: `${left}px`,
        width: `min(${popoverWidth}px, calc(100vw - 32px))`,
        zIndex: 100000
      };
      arrowDirection = 'up';
      arrowStyle = {
        left: `${Math.max(24, Math.min(popoverWidth - 34, targetCenterX - left))}px`,
        top: '-8px'
      };
    } else if (placement === 'top') {
      const top = Math.max(16, targetRect.top - 340);
      const left = Math.max(16, Math.min(viewportWidth - popoverWidth - 16, targetCenterX - popoverWidth / 2));
      popoverStyle = {
        position: 'fixed',
        top: `${top}px`,
        left: `${left}px`,
        width: `min(${popoverWidth}px, calc(100vw - 32px))`,
        zIndex: 100000
      };
      arrowDirection = 'down';
      arrowStyle = {
        left: `${Math.max(24, Math.min(popoverWidth - 34, targetCenterX - left))}px`,
        bottom: '-8px'
      };
    } else if (placement === 'right') {
      const left = Math.min(targetRect.right + padding, viewportWidth - popoverWidth - 16);
      const top = Math.max(16, Math.min(viewportHeight - 340, targetCenterY - 120));
      popoverStyle = {
        position: 'fixed',
        top: `${top}px`,
        left: `${left}px`,
        width: `min(${popoverWidth}px, calc(100vw - 32px))`,
        zIndex: 100000
      };
      arrowDirection = 'left';
      arrowStyle = {
        top: `${Math.max(24, Math.min(280, targetCenterY - top))}px`,
        left: '-8px'
      };
    } else if (placement === 'left') {
      const left = Math.max(16, targetRect.left - popoverWidth - padding);
      const top = Math.max(16, Math.min(viewportHeight - 340, targetCenterY - 120));
      popoverStyle = {
        position: 'fixed',
        top: `${top}px`,
        left: `${left}px`,
        width: `min(${popoverWidth}px, calc(100vw - 32px))`,
        zIndex: 100000
      };
      arrowDirection = 'right';
      arrowStyle = {
        top: `${Math.max(24, Math.min(280, targetCenterY - top))}px`,
        right: '-8px'
      };
    }
  }

  return (
    <>
      {/* 1. SPOTLIGHT FULLSCREEN OVERLAY & CUTOUT (Calm, non-flickering) */}
      <div className="fixed inset-0 z-[99998] pointer-events-none transition-all duration-300">
        {targetRect ? (
          // Focused element highlight with stable dark backdrop
          <div
            className="absolute rounded-2xl ring-2 ring-primary ring-offset-2 ring-offset-black/50 transition-all duration-300 pointer-events-none shadow-[0_0_0_9999px_rgba(0,0,0,0.68)]"
            style={{
              top: `${Math.max(0, targetRect.top - 6)}px`,
              left: `${Math.max(0, targetRect.left - 6)}px`,
              width: `${targetRect.width + 12}px`,
              height: `${targetRect.height + 12}px`
            }}
          />
        ) : (
          // Center dialog backdrop
          <div className="fixed inset-0 bg-black/65 backdrop-blur-xs pointer-events-auto transition-opacity duration-300" onClick={handleDismissLater} />
        )}
      </div>

      {/* 2. FLOATING POPOVER CARD WITH POINTER ARROW */}
      <div 
        style={popoverStyle}
        className="rounded-3xl bg-surface-containerLowest border border-primary/30 shadow-2xl text-surface-on p-5 sm:p-6 animate-scale-in relative pointer-events-auto"
      >
        {/* Pointer Arrow */}
        {arrowDirection === 'up' && (
          <div 
            style={arrowStyle} 
            className="absolute w-4 h-4 bg-surface-containerLowest border-t border-l border-primary/30 rotate-45 transform -translate-x-1/2"
          />
        )}
        {arrowDirection === 'down' && (
          <div 
            style={arrowStyle} 
            className="absolute w-4 h-4 bg-surface-containerLowest border-b border-r border-primary/30 rotate-45 transform -translate-x-1/2"
          />
        )}
        {arrowDirection === 'left' && (
          <div 
            style={arrowStyle} 
            className="absolute w-4 h-4 bg-surface-containerLowest border-b border-l border-primary/30 rotate-45 transform -translate-y-1/2"
          />
        )}
        {arrowDirection === 'right' && (
          <div 
            style={arrowStyle} 
            className="absolute w-4 h-4 bg-surface-containerLowest border-t border-r border-primary/30 rotate-45 transform -translate-y-1/2"
          />
        )}

        {/* Card Header */}
        <div className="flex items-center justify-between gap-3 pb-3 border-b border-outlineVariant/20 mb-3.5">
          <div className="flex items-center gap-2">
            <div className="w-7 h-7 rounded-xl bg-primary/10 text-primary flex items-center justify-center">
              <StepIcon className="w-4 h-4" />
            </div>
            <span className="text-[11px] font-bold uppercase tracking-wider text-primary">
              {activeStep.badge}
            </span>
          </div>

          <div className="flex items-center gap-1">
            <button
              onClick={() => setIsMinimized(true)}
              className="p-1.5 text-surface-onVariant hover:text-surface-on hover:bg-surface-containerHigh rounded-lg transition-colors cursor-pointer"
              title="Tour minimieren (Esc)"
            >
              <Minimize2 className="w-4 h-4" />
            </button>
            <button
              onClick={handleDismissLater}
              className="p-1.5 text-surface-onVariant hover:text-surface-on hover:bg-surface-containerHigh rounded-lg transition-colors cursor-pointer"
              title="Tour beenden"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Card Content */}
        <div className="space-y-2 mb-4">
          <h3 className="text-base sm:text-lg font-bold text-surface-on leading-snug">
            {activeStep.title}
          </h3>
          <p className="text-xs sm:text-sm text-surface-onVariant leading-relaxed">
            {activeStep.text}
          </p>

          {activeStep.tip && (
            <div className="p-3 rounded-xl bg-primary/5 border border-primary/15 text-[11px] font-medium text-surface-on leading-snug flex items-start gap-2.5 mt-2.5">
              <Info className="w-4 h-4 text-primary shrink-0 mt-0.5" />
              <span className="text-surface-onVariant">{activeStep.tip}</span>
            </div>
          )}
        </div>

        {/* Step Progress Dots */}
        <div className="flex items-center justify-center gap-1.5 py-1 mb-3">
          {steps.map((s, idx) => (
            <button
              key={s.id}
              onClick={() => handleStepJump(idx)}
              className={`h-1.5 rounded-full transition-all cursor-pointer ${
                idx === currentStep
                  ? 'w-6 bg-primary'
                  : idx < currentStep
                  ? 'w-2 bg-primary/40 hover:bg-primary/60'
                  : 'w-2 bg-outlineVariant/40 hover:bg-outlineVariant'
              }`}
              title={`Zu Schritt ${idx + 1}: ${s.title}`}
            />
          ))}
        </div>

        {/* Card Footer Navigation & Devino Question */}
        <div className="flex items-center justify-between gap-2 pt-3 border-t border-outlineVariant/20">
          <button
            onClick={handleOpenQuestionModal}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-surface-containerHigh hover:bg-surface-containerHighest text-surface-onVariant hover:text-surface-on text-xs font-semibold transition-all cursor-pointer"
            title="Frage zum aktuellen Schritt stellen"
          >
            <MessageSquare className="w-3.5 h-3.5 text-primary" />
            <span>Frage stellen</span>
          </button>

          <div className="flex items-center gap-2">
            {currentStep > 0 && (
              <button
                onClick={handlePrev}
                className="flex items-center gap-1 px-3 py-1.5 rounded-xl bg-surface-containerHigh hover:bg-surface-containerHighest text-surface-on text-xs font-bold transition-colors cursor-pointer"
              >
                <ChevronLeft className="w-3.5 h-3.5" />
                <span>Zurück</span>
              </button>
            )}

            <button
              onClick={handleNext}
              className="px-4 py-2 rounded-xl bg-primary text-primary-on text-xs font-bold shadow-sm hover:bg-primary/90 transition-all flex items-center gap-1.5 cursor-pointer hover:scale-[1.02]"
            >
              <span>{activeStep.btnNextText}</span>
              <ChevronRight className="w-3.5 h-3.5" />
            </button>
          </div>
        </div>
      </div>

      {/* ========================================================= */}
      {/* 3. MODAL: FRAGE AN DEVINO / FEEDBACK */}
      {/* ========================================================= */}
      {isQuestionModalOpen && (
        <div 
          className="fixed inset-0 z-[100001] flex items-center justify-center p-4 bg-surface-scrim/70 backdrop-blur-xs animate-fade-in pointer-events-auto"
          onClick={(e) => e.stopPropagation()}
        >
          <div 
            className="w-full max-w-lg rounded-3xl bg-surface-containerHighest border border-outlineVariant/40 shadow-2xl p-6 relative overflow-hidden text-surface-on max-h-[92vh] flex flex-col justify-between animate-scale-in"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start justify-between gap-4 pb-4 border-b border-outlineVariant/20">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-2xl bg-primary/15 text-primary flex items-center justify-center shadow-inner">
                  <MessageSquare className="w-5 h-5" />
                </div>
                <div>
                  <h3 className="text-base font-bold text-surface-on flex items-center gap-2">
                    Frage zur Einrichtung
                  </h3>
                  <p className="text-xs text-surface-onVariant">
                    Betrifft: <strong className="text-surface-on">{activeStep.title}</strong>
                  </p>
                </div>
              </div>
              <button
                onClick={() => setIsQuestionModalOpen(false)}
                className="p-1.5 rounded-full text-surface-onVariant hover:text-surface-on hover:bg-surface-on/5 transition-colors cursor-pointer"
                title="Schließen"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {questionSuccess ? (
              <div className="py-10 flex flex-col items-center justify-center text-center space-y-3 animate-fade-in">
                <div className="w-16 h-16 rounded-full bg-emerald-500/15 text-emerald-500 flex items-center justify-center">
                  <CheckCircle2 className="w-8 h-8" />
                </div>
                <h4 className="text-lg font-bold text-surface-on">Nachricht übermittelt</h4>
                <p className="text-xs text-surface-onVariant max-w-sm leading-relaxed">
                  Deine Frage zu <strong>"{activeStep.title}"</strong> wurde erfolgreich an Devino weitergeleitet.
                </p>
                <div className="pt-4 flex flex-col sm:flex-row gap-2 w-full max-w-xs">
                  <button
                    onClick={() => {
                      setIsQuestionModalOpen(false);
                      setIsMinimized(true);
                    }}
                    className="flex-1 py-2.5 px-4 rounded-xl bg-surface-containerLow text-surface-on text-xs font-bold hover:bg-surface-containerHigh transition-colors cursor-pointer"
                  >
                    Tour minimieren
                  </button>
                  <button
                    onClick={() => {
                      setIsQuestionModalOpen(false);
                    }}
                    className="flex-1 py-2.5 px-4 rounded-xl bg-primary text-primary-on text-xs font-bold hover:bg-primary/90 transition-colors cursor-pointer"
                  >
                    Tour fortsetzen
                  </button>
                </div>
              </div>
            ) : (
              <form onSubmit={handleSubmitQuestion} className="space-y-4 pt-4 overflow-y-auto">
                <div className="p-3 rounded-2xl bg-surface-containerLow border border-outlineVariant/20 text-xs text-surface-onVariant leading-relaxed flex items-start gap-2.5">
                  <Info className="w-4 h-4 text-primary shrink-0 mt-0.5" />
                  <span>Deine Frage wird mit dem genauen Kontext des aktuellen Schritts (<em>{activeStep.title}</em>) und der Seiten-URL übermittelt.</span>
                </div>

                <div className="space-y-1.5">
                  <label className="text-xs font-bold text-surface-onVariant block">
                    Deine Frage oder Anliegen:
                  </label>
                  <textarea
                    rows={4}
                    value={questionText}
                    onChange={(e) => setQuestionText(e.target.value)}
                    placeholder="Beschreibe kurz, an welcher Stelle du Unterstützung benötigst..."
                    className="w-full bg-surface-containerLow text-surface-on px-3.5 py-2.5 rounded-2xl text-xs border border-outlineVariant/30 focus:border-primary/60 focus:outline-none transition-colors resize-none placeholder:text-surface-onVariant/50"
                  />
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                  <div>
                    <label className="text-[11px] font-medium text-surface-onVariant block mb-1">
                      Dein Name
                    </label>
                    <input
                      type="text"
                      value={authorName}
                      onChange={(e) => setAuthorName(e.target.value)}
                      placeholder="Name"
                      className="w-full bg-surface-containerLow text-surface-on px-3 py-2 rounded-xl text-xs border border-outlineVariant/30 focus:border-primary/60 focus:outline-none"
                    />
                  </div>
                  <div>
                    <label className="text-[11px] font-medium text-surface-onVariant block mb-1">
                      E-Mail für Antwort
                    </label>
                    <input
                      type="email"
                      value={authorEmail}
                      onChange={(e) => setAuthorEmail(e.target.value)}
                      placeholder="deine-email@beispiel.de"
                      className="w-full bg-surface-containerLow text-surface-on px-3 py-2 rounded-xl text-xs border border-outlineVariant/30 focus:border-primary/60 focus:outline-none"
                    />
                  </div>
                </div>

                {questionError && (
                  <p className="text-xs text-rose-500 font-semibold text-center bg-rose-500/10 p-2 rounded-xl border border-rose-500/20">
                    {questionError}
                  </p>
                )}

                <div className="flex items-center justify-between gap-3 pt-3 border-t border-outlineVariant/20">
                  <button
                    type="button"
                    onClick={() => setIsQuestionModalOpen(false)}
                    className="px-4 py-2 text-xs font-semibold text-surface-onVariant hover:text-surface-on rounded-xl hover:bg-surface-on/5 transition-colors cursor-pointer"
                  >
                    Abbrechen
                  </button>
                  <button
                    type="submit"
                    disabled={isSubmittingQuestion}
                    className="px-5 py-2.5 rounded-2xl bg-primary hover:bg-primary/90 text-primary-on font-bold text-xs shadow-md transition-all flex items-center gap-2 cursor-pointer disabled:opacity-50"
                  >
                    {isSubmittingQuestion ? (
                      <>
                        <div className="w-3.5 h-3.5 rounded-full border-2 border-primary-on border-t-transparent animate-spin" />
                        <span>Wird gesendet...</span>
                      </>
                    ) : (
                      <>
                        <Send className="w-3.5 h-3.5" />
                        <span>Frage an Devino senden</span>
                      </>
                    )}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      )}
    </>
  );
}
